import { ReactElement, ReactNode } from 'react';
import { Provider, useSelector } from 'react-redux';
import { Navigate, Route, Routes } from 'react-router';
import { StylesProvider } from '@mui/styles';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import moment from 'moment-timezone';

// Components
import AuthenticatedLayoutRoute from 'components/global/routing/AuthenticatedLayoutRoute';
import DownloadFile from 'components/DownloadFile/DownloadFile';
import { DownloadLearnerFile } from './components/DownloadFile/DownloadLearnerFile';
import { DownloadLearnerSearchFile } from './components/DownloadFile/DownloadLearnerSearchFile';

// Styles
import './index.scss';
import { commonTheme } from 'globals/themes/common-theme';

// Layouts/Pages
import { AuthenticatedLayout } from 'layouts/AuthenticatedLayout';
import { DashboardPage } from 'layouts/pages/dashboard';
import DashboardAgreementPage from 'layouts/pages/dashboard/agreement';
import { ActivitySummaryPage } from 'layouts/pages/activity/summary';
import ActivityDetailPage from 'layouts/pages/activity/detail';
import ActivityEditPage from 'layouts/pages/activity/edit';
import { AddActivityPage } from 'layouts/pages/activity/add-activity';
import BatchActivityPage from 'layouts/pages/activity/batch';
import BatchActivityHistoryPage from 'layouts/pages/activity/batch/history';
import { LearnerSummaryPage } from 'layouts/pages/learner/summary';
import { AddLearnerPage } from 'layouts/pages/learner/add-learner';
import { LearnerDetailPage } from 'layouts/pages/learner/detail';
import { LearnersSummaryDetailPage } from 'layouts/pages/learner/detail/ByActivity';
import { LearnerSearchPage } from 'layouts/pages/learner/search';
import { BatchLearnerPage } from 'layouts/pages/learner/batch';
import { BatchLearnerHistoryPage } from 'layouts/pages/learner/batch/history';
import { LearnerValidationPage } from 'layouts/pages/learner/validation';
import { LearnerValidationToolBatchPage } from 'layouts/pages/learner/validation/batch';
import { BatchLearnerValidationHistoryPage } from 'layouts/pages/learner/validation/batch/history';
import { ReportPage } from 'layouts/pages/report';
import { ReportViewPage } from 'layouts/pages/report/view';
import { BCTPage } from 'layouts/pages/bct';
import { BoardListPage } from 'layouts/pages/boardlist';
import { ProgramSummary } from 'layouts/pages/dashboard/program-summary';
import { AdminPage } from 'layouts/pages/admin';
import { AccountManagerPage } from 'layouts/pages/account-manager';
import { BoardProcessingMetricsPage } from 'layouts/pages/admin/board-processing-metrics';
import { ProviderSubmissionLogsPage } from 'layouts/pages/admin/provider-submission-logs';
import { SearchSynonymsPage } from 'layouts/pages/admin/search-synonyms';
import { AdminCommandsPage } from 'layouts/pages/admin/admin-commands';
import { CmePassportImpersonationPage } from 'layouts/pages/admin/cme-passport-impersonation';
import { CreateWebServiceAccount } from './layouts/pages/admin/create-web-service-account';
import ClientCredentialsPage from './layouts/pages/clientCredentials';
import { SetContextPage } from 'layouts/pages/system/SetContextPage';

// Store
import { store } from 'store';
import { NotFoundPage } from 'layouts/pages/notfound';
import { HelpDeskPage } from 'layouts/pages/helpdesk';
import { DEFAULT_TIMEZONE } from './core/constants';
import { SmsSummaryReportPage } from './layouts/pages/report/SummaryReport';
import classNames from 'classnames';
import { getSite } from 'utils/site';
import { rollupOrganizationEnumSelector } from 'store/user/selectors';
import { RollupOrganizationEnums } from 'core/models';
import { SiteHeadManager } from 'components/SiteHeadManager/SiteHeadManager';
import { AccountManagerAddEditUserPage } from 'layouts/pages/account-manager/add-edit-user';
import { AccountManagerEditOrganizationPage } from 'layouts/pages/account-manager/edit-organization';
import { AccountManagerAddEditAccreditationPage } from 'layouts/pages/account-manager/add-edit-accreditation';
import { AccountManagerAddEditProviderPage } from 'layouts/pages/account-manager/add-edit-provider';

// Force all moment timezones to match the default. You should still be able to use moment like normal in any ts/tsx files.
moment.tz.setDefault(DEFAULT_TIMEZONE);

// placeholder for /activities/add-activity that just redirects to the real page
const AddActivityPlaceholder = () => <Navigate to="/activities/add-activity/new" />;

const Container = ({ children }: { children: ReactNode }): ReactElement => {
  const site = getSite();
  const rollupOrganizationEnum = useSelector(rollupOrganizationEnumSelector);
  const className = classNames('App', {
    'site-ja': rollupOrganizationEnum === RollupOrganizationEnums.JA,
    'site-nars': site === 'NARS',
    'site-pars': site === 'PARS',
  });
  return <div className={className}>{children}</div>;
};

const App = (): ReactNode => {
  return (
    <>
      <SiteHeadManager />
      <Provider store={store}>
        <StylesProvider injectFirst>
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={commonTheme}>
              <Container>
                <Routes>
                  <Route
                    path="/"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <DashboardPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  {/** this page will set some values then immediately redirect to /, so it doesn't do auth or have a layout **/}
                  <Route element={<SetContextPage />} path="/set-context/:userId/:orgId?/:providerId?" />
                  <Route
                    path="bct/:id"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <BCTPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <DashboardPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/dashboard/agreement"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <DashboardAgreementPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/dashboard/summary"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <ProgramSummary />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities"
                    element={
                      <AuthenticatedLayoutRoute
                        layout={AuthenticatedLayout}
                        render={() => <Navigate to="/activities/summary" />}
                      />
                    }
                  />
                  <Route
                    path="/activities/summary"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <ActivitySummaryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/board-list"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <BoardListPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <AdminPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/accreditations/add"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditAccreditationPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/accreditations/edit/:id"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditAccreditationPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/provider/add"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditProviderPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/provider/edit/:id"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditProviderPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/users/add"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditUserPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/edit-organization/:id"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerEditOrganizationPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/account-manager/users/edit/:id"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <AccountManagerAddEditUserPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/board-processing-metrics"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <BoardProcessingMetricsPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/provider-submission-logs"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <ProviderSubmissionLogsPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/admin-commands"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <AdminCommandsPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/create-web-service-account"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout} hideSubnav isStaffOnly>
                        <CreateWebServiceAccount />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/api/client-credentials"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout} hideSubnav>
                        <ClientCredentialsPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/search-synonyms"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <SearchSynonymsPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/admin/cme-passport-impersonation"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav isStaffOnly layout={AuthenticatedLayout}>
                        <CmePassportImpersonationPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/summary"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <ActivitySummaryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/detail/:id"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <ActivityDetailPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/edit/:id"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <ActivityEditPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/:activityId/add-learner"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <AddLearnerPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/add-activity"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <AddActivityPlaceholder />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/add-activity/new"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <AddActivityPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/batch/history"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchActivityHistoryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/batches/downloadtemplate/:type"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchActivityPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/activities/batch"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchActivityPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners"
                    element={
                      <AuthenticatedLayoutRoute
                        layout={AuthenticatedLayout}
                        render={() => <Navigate to="/learners/summary" />}
                      />
                    }
                  />
                  <Route
                    path="/learners/summary"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerSummaryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/summary/detail/:key"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnersSummaryDetailPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/:activityId/learnercompletions/bylearner/:ulid"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerDetailPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/:activityId/learnercompletions/bycompletion/:completionId"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerDetailPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/search"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerSearchPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/batch/history"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchLearnerHistoryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/batches/downloadtemplate/:type"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchLearnerPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/batch"
                    element={
                      <AuthenticatedLayoutRoute isBoardUserAllowed={false} layout={AuthenticatedLayout}>
                        <BatchLearnerPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/validation"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerValidationPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/validation/batch"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <LearnerValidationToolBatchPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/learners/validation/batch/history"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <BatchLearnerValidationHistoryPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <ReportPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/reports/summaryreports"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <SmsSummaryReportPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/reports/view/:userId/:reportId"
                    element={
                      <AuthenticatedLayoutRoute layout={AuthenticatedLayout}>
                        <ReportViewPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="/contact-support"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav allowInvalidUser layout={AuthenticatedLayout}>
                        <HelpDeskPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                  <Route
                    path="*"
                    element={
                      <AuthenticatedLayoutRoute hideSubnav layout={AuthenticatedLayout}>
                        <NotFoundPage />
                      </AuthenticatedLayoutRoute>
                    }
                  />
                </Routes>
                <DownloadFile />
                <DownloadLearnerFile />
                <DownloadLearnerSearchFile />
              </Container>
            </ThemeProvider>
          </StyledEngineProvider>
        </StylesProvider>
      </Provider>
    </>
  );
};

export default App;
